import {
    FGButton,
    FGIcons,
    FGLink,
    FGModal,
    FGStack,
    FGTile,
    FGTypography,
} from '@fg/design-system';
import { InsightAndEventSeparator } from 'components/eventsFeed/InsightAndEventSeparator';
import { isEmpty } from 'lodash/fp';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClusterRelatedArticle } from '../../interfaces';
import FrameIcon from '../../assets/svg/Frame.svg?react';

const SCORE_TYPE_ORDER = {
    human: 0,
    machine: 1,
};

type Props = {
    articles?: ClusterRelatedArticle[];
    previewCount?: number;
    getArticleUrl: (articleId: number) => string;
};

const SimilarArticles = React.memo((props: Props) => {
    const { articles, previewCount = 3, getArticleUrl } = props;
    const navigate = useNavigate();
    const [showAllArticles, setShowAllArticles] = useState(false);
    const closeModal = useCallback(() => setShowAllArticles(false), []);

    const goToArticle = useCallback(
        (article: ClusterRelatedArticle) => {
            setShowAllArticles(false);
            navigate(getArticleUrl(article.id));
        },
        [navigate, getArticleUrl]
    );

    if (isEmpty(articles)) {
        return null;
    }

    const sortedArticles = articles.slice().sort(
        (firstArticle, secondArticle) =>
            (SCORE_TYPE_ORDER[firstArticle.score_type] ?? 2) -
            (SCORE_TYPE_ORDER[secondArticle.score_type] ?? 2)
    );

    const renderArticleLink = (
        article: ClusterRelatedArticle,
        isRenderIcon = false
    ) => (
        <FGLink
            key={article.id}
            className="cluster-article-link"
            style={{ color: 'var(--fg-color-base-blue-12)' }}
            isButton
            onClick={() => goToArticle(article)}>
            <FGStack gap={8} alignItems="center">
                {isRenderIcon && article.score_type && (
                    <FGTile
                        padding="small"
                        style={{
                            borderRadius: 3,
                            height: 20,
                            width: 20,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: 0,
                            color: 'inherit',
                        }}>
                        {article.score_type === 'machine' ? (
                            <FrameIcon style={{ width: 12, height: 12 }} />
                        ) : (
                            <FGIcons icon="Contact" size={12} />
                        )}
                    </FGTile>
                )}
                <FGTypography
                    tag="label"
                    variant="link2"
                    alignment="left"
                    cssStyle={{ cursor: 'pointer' }}>
                    {article.headline}
                </FGTypography>
                <FGIcons icon="ArrowRight" size={14} />
            </FGStack>
        </FGLink>
    );

    const renderAllArticlesModal = () => {
        if (!showAllArticles) {
            return null;
        }
        return (
            <FGModal
                className="cluster-articles-modal"
                isOpen
                header={`All articles (${articles.length})`}
                onRequestClose={closeModal}
                footerActionComponents={[
                    <FGButton variant="secondary" onClick={closeModal}>
                        Close
                    </FGButton>,
                ]}>
                <FGStack
                    direction="column"
                    gap={8}
                    alignItems="flex-start"
                    style={{
                        height: 340,
                        overflowY: 'auto',
                        paddingInline: 18,
                    }}>
                    {sortedArticles.map(article =>
                        renderArticleLink(article, true)
                    )}
                </FGStack>
            </FGModal>
        );
    };

    return (
        <>
            <InsightAndEventSeparator />
            <FGStack direction="column">
                <FGTypography
                    tag="label"
                    variant="label3"
                    cssStyle={{ color: 'var(--fg-color-base-gray-11)' }}>
                    Similar Articles
                </FGTypography>
                <FGStack direction="column" gap={8} alignItems="flex-start">
                    {sortedArticles
                        .slice(0, previewCount)
                        .map(article => renderArticleLink(article))}
                    {articles.length > previewCount && (
                        <FGLink isButton onClick={() => setShowAllArticles(true)}>
                            <FGTypography
                                tag="label"
                                variant="link2"
                                cssStyle={{
                                    cursor: 'pointer',
                                    color: 'var(--fg-color-base-blue-12)',
                                }}>
                                {`See all articles (${articles.length})`}
                            </FGTypography>
                        </FGLink>
                    )}
                </FGStack>
                {renderAllArticlesModal()}
            </FGStack>
        </>
    );
});

export default SimilarArticles;
